import React, { useState, useEffect } from 'react'
import {
  Server,
  Wifi,
  Shield,
  Globe,
  Clock,
  Zap,
  Filter,
  Search,
  Eye,
  RefreshCw,
  BarChart3,
  Play,
  Pause,
  Download,
  Copy,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { Card, CardHeader, CardContent } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import { StatusBadge } from '../components/ui/Badge'
import { Select } from '../components/ui/Input'
import { timeAgo, formatNumber, cn, copyToClipboard } from '../utils/cn'

const protocolColors: Record<string, string> = {
  vless: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  vmess: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  trojan: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  shadowsocks: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  unknown: 'bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-400',
}

const protocolLabel = (protocol?: string) => String(protocol || 'unknown').toUpperCase()
const protocolClass = (protocol?: string) => protocolColors[String(protocol || 'unknown').toLowerCase()] || protocolColors.unknown

const locationColors: Record<string, string> = {
  'United States': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  'Japan': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  'Singapore': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  'United Kingdom': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  'Germany': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  'France': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
  'Canada': 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400',
  'Australia': 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400',
}

const getLatencyColor = (latency: number) => {
  if (latency < 50) return 'text-success-600 dark:text-success-400'
  if (latency < 100) return 'text-warning-600 dark:text-warning-400'
  return 'text-error-600 dark:text-error-400'
}

const getBandwidthColor = (bandwidth: number) => {
  if (bandwidth > 1000) return 'text-success-600 dark:text-success-400'
  if (bandwidth > 500) return 'text-warning-600 dark:text-warning-400'
  return 'text-error-600 dark:text-error-400'
}

const NodeConfigModal = ({ node, isOpen, onClose }: { node: any; isOpen: boolean; onClose: () => void }) => {
  const [copied, setCopied] = useState(false)
  const [configFormat, setConfigFormat] = useState('clash')

  const getNodeConfig = () => {
    const baseConfig = {
      name: node.name,
      type: node.protocol,
      server: node.server,
      port: node.port,
      uuid: node.uuid,
      password: node.password,
      method: node.method,
      network: node.network,
      tls: node.tls,
      sni: node.sni,
      host: node.host,
      path: node.path,
      serviceName: node.serviceName,
    }

    switch (configFormat) {
      case 'clash':
        return `proxies:
  - name: ${node.name}
    type: ${node.protocol}
    server: ${node.server}
    port: ${node.port}
    uuid: ${node.uuid || ''}
    password: ${node.password || ''}
    cipher: ${node.method || 'auto'}
    network: ${node.network}
    tls: ${node.tls}
    servername: ${node.sni || ''}
    ws-opts:
      path: ${node.path || ''}
      headers:
        Host: ${node.host || ''}
    grpc-opts:
      grpc-service-name: ${node.serviceName || ''}`

      case 'raw':
        if (node.protocol === 'vless') {
          return `vless://${node.uuid}@${node.server}:${node.port}?type=${node.network}&security=tls&sni=${node.sni}&path=${encodeURIComponent(node.path || '')}&host=${node.host}#${encodeURIComponent(node.name)}`
        } else if (node.protocol === 'vmess') {
          const vmessConfig = {
            v: '2',
            ps: node.name,
            add: node.server,
            port: node.port,
            id: node.uuid,
            aid: '0',
            net: node.network,
            type: 'none',
            host: node.host,
            path: node.path,
            tls: node.tls ? 'tls' : 'none',
            sni: node.sni,
          }
          return `vmess://${btoa(JSON.stringify(vmessConfig))}`
        } else if (node.protocol === 'trojan') {
          return `trojan://${node.password}@${node.server}:${node.port}?security=tls&sni=${node.sni}#${encodeURIComponent(node.name)}`
        } else {
          return `ss://${btoa(`${node.method || 'aes-256-gcm'}:${node.password}`)}@${node.server}:${node.port}#${encodeURIComponent(node.name)}`
        }

      case 'json':
        return JSON.stringify(baseConfig, null, 2)

      default:
        return ''
    }
  }

  const handleCopyConfig = async () => {
    const success = await copyToClipboard(getNodeConfig())
    if (success) {
      setCopied(true)
      toast.success('Configuration copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } else {
      toast.error('Failed to copy configuration')
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Node Configuration"
      description="Copy or export node configuration"
      size="lg"
    >
      <div className="space-y-6">
        {/* Node Info Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Protocol</div>
            <div className="font-medium text-secondary-900 dark:text-white">
              <span className={cn("px-2 py-1 rounded text-xs", protocolClass(node.protocol))}>
                {protocolLabel(node.protocol)}
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Location</div>
            <div className="font-medium text-secondary-900 dark:text-white">
              <span className={cn("px-2 py-1 rounded text-xs", locationColors[node.location])}>
                {node.location}
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Status</div>
            <StatusBadge status={node.status} size="sm" />
          </div>
          <div className="space-y-1">
            <div className="text-sm text-secondary-500 dark:text-secondary-400">Provider</div>
            <div className="font-medium text-secondary-900 dark:text-white">{node.provider}</div>
          </div>
        </div>

        {/* Configuration Format */}
        <div>
          <div className="text-sm font-medium text-secondary-900 dark:text-white mb-3">
            Configuration Format
          </div>
          <div className="flex flex-wrap gap-2">
            {['clash', 'raw', 'json'].map((format) => (
              <button
                key={format}
                onClick={() => setConfigFormat(format)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  configFormat === format
                    ? "bg-primary-500 text-white"
                    : "bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-400 hover:bg-secondary-200 dark:hover:bg-secondary-700"
                )}
              >
                {format.charAt(0).toUpperCase() + format.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Configuration Preview */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-secondary-900 dark:text-white">
              Configuration
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopyConfig}
              leftIcon={copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <div className="p-4 rounded-lg bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700">
            <pre className="text-xs whitespace-pre-wrap break-words font-mono overflow-auto max-h-64">
              {getNodeConfig()}
            </pre>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
          <Button variant="ghost" size="sm" leftIcon={<Download className="w-4 h-4" />}>
            Export as File
          </Button>
          <Button variant="ghost" size="sm" leftIcon={<RefreshCw className="w-4 h-4" />}>
            Validate Now
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const Modal = ({ isOpen, onClose, title, description, size = 'md', children }: any) => {
  if (!isOpen) return null

  const sizeClasses: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={cn(
        "relative bg-surface-light dark:bg-surface-dark rounded-xl shadow-modal z-50",
        "border border-secondary-200 dark:border-secondary-700 max-h-[90vh] overflow-auto",
        sizeClasses[size]
      )}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                {title}
              </h2>
              {description && (
                <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                  {description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 text-secondary-500 dark:text-secondary-400"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

const Nodes = () => {
  const [search, setSearch] = useState('')
  const [protocolFilter, setProtocolFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [providerFilter, setProviderFilter] = useState('all')
  const [selectedNode, setSelectedNode] = useState<any>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const { data: nodes = [], isLoading } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token')
      const response = await fetch('/api/v1/nodes/', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!response.ok) throw new Error('Failed to fetch nodes')
      return response.json()
    },
  })

  // Filter nodes
  const filteredNodes = nodes.filter((node: any) => {
    const matchesSearch =
      node.name.toLowerCase().includes(search.toLowerCase()) ||
      (node.server || node.address || '').toLowerCase().includes(search.toLowerCase()) ||
      (node.location || '').toLowerCase().includes(search.toLowerCase())

    const matchesProtocol = protocolFilter === 'all' || node.protocol === protocolFilter
    const matchesStatus = statusFilter === 'all' || node.status === statusFilter
    const matchesProvider = providerFilter === 'all' || node.provider === providerFilter

    return matchesSearch && matchesProtocol && matchesStatus && matchesProvider
  })

  // Calculate statistics
  const stats = {
    total: filteredNodes.length,
    active: filteredNodes.filter((n: any) => n.status === 'active').length,
    unsupported: filteredNodes.filter((n: any) => n.status === 'unsupported').length,
    error: filteredNodes.filter((n: any) => ['error', 'failed'].includes(n.status)).length,
    providers: new Set(filteredNodes.map((n: any) => n.provider || 'Unknown')).size,
  }

  const handleTestNode = async (node: any) => {
    toast.error(`Node testing is not implemented yet for ${node.name}. No fake latency will be shown.`)
  }

  const handleViewConfig = (node: any) => {
    setSelectedNode(node)
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 rounded-lg bg-secondary-200 dark:bg-secondary-700" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
          ))}
        </div>
        <div className="h-96 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Nodes Management
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400 mt-1">
            View and manage VPN nodes from all providers
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            leftIcon={viewMode === 'grid' ? <BarChart3 className="w-4 h-4" /> : <Server className="w-4 h-4" />}
          >
            {viewMode === 'grid' ? 'Grid View' : 'List View'}
          </Button>
          <Button
            variant="primary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh All
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search nodes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={protocolFilter}
              onChange={(e) => setProtocolFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Protocols' },
                { value: 'vless', label: 'VLESS' },
                { value: 'vmess', label: 'VMESS' },
                { value: 'trojan', label: 'Trojan' },
                { value: 'shadowsocks', label: 'Shadowsocks' },
              ]}
              className="w-40"
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'unsupported', label: 'Unsupported' },
                { value: 'warning', label: 'Warning' },
                { value: 'error', label: 'Error' },
                { value: 'failed', label: 'Failed' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              className="w-40"
            />
            <Select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Providers' },
                ...Array.from(new Set<string>(nodes.map((n: any) => String(n.provider || 'Unknown')))).map((p: string) => ({ value: p, label: p }))
              ]}
              className="w-40"
            />
            <Button variant="ghost" size="sm" leftIcon={<Filter className="w-4 h-4" />}>
              More Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {stats.total}
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Total Nodes
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400">
              {stats.active}
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Active Nodes
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
              {stats.unsupported}
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Unsupported
            </div>
          </div>
        </Card>
        <Card className="text-center">
          <div className="p-4">
            <div className="text-2xl font-bold text-info-600 dark:text-info-400">
              {stats.providers}
            </div>
            <div className="text-sm text-secondary-500 dark:text-secondary-400">
              Providers
            </div>
          </div>
        </Card>
      </div>

      {/* Nodes Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredNodes.map((node: any) => (
            <Card key={node.id} className="hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-0">
                <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-secondary-900 dark:text-white truncate">
                        {node.name}
                      </h3>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                        {node.server}:{node.port}
                      </p>
                    </div>
                    <StatusBadge status={node.status} size="sm" />
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">Protocol</span>
                    <span className={cn("px-2 py-0.5 rounded text-xs", protocolClass(node.protocol))}>
                      {protocolLabel(node.protocol)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">Address</span>
                    <span className="text-sm font-medium text-secondary-900 dark:text-white">
                      {node.server || node.address || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">Provider</span>
                    <span className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                      {node.provider}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">Latency</span>
                    <span className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
                      Not measured
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">Bandwidth</span>
                    <span className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
                      Not measured
                    </span>
                  </div>
                </div>

                <div className="p-4 border-t border-secondary-200 dark:border-secondary-700">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewConfig(node)}
                      leftIcon={<Eye className="w-3 h-3" />}
                    >
                      Config
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTestNode(node)}
                      leftIcon={<Zap className="w-3 h-3" />}
                    >
                      Test
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader
            title="Nodes List"
            description={`Showing ${filteredNodes.length} of ${nodes.length} nodes`}
          />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200 dark:border-secondary-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                    Node
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                    Protocol
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                    Provider
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                    Imported
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-secondary-900 dark:text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                {filteredNodes.map((node: any) => (
                  <tr key={node.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-secondary-900 dark:text-white">
                          {node.name}
                        </div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate max-w-xs">
                          {node.server}:{node.port}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn("px-2 py-1 rounded text-xs", protocolClass(node.protocol))}>
                        {protocolLabel(node.protocol)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-secondary-700 dark:text-secondary-300">
                        {node.provider || 'Unknown'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={node.status} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-secondary-400" />
                        <span className="text-sm">{node.created_at ? timeAgo(node.created_at) : 'N/A'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewConfig(node)}
                          leftIcon={<Eye className="w-3 h-3" />}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTestNode(node)}
                          leftIcon={<Zap className="w-3 h-3" />}
                        >
                          Test
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Configuration Modal */}
      {selectedNode && (
        <NodeConfigModal
          node={selectedNode}
          isOpen={!!selectedNode}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  )
}

export default Nodes
